// src/pages/departments/FoodScience.js
import { motion } from "framer-motion";

const FoodScience = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center">
        <img
          src="/images/fstb.jpeg"
          alt="Department Banner"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative text-center text-white px-6">
          <motion.h1
            className="text-3xl md:text-5xl font-extrabold mb-3"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Department of Food Science and Technology
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Excellence in Teaching, Research, and Innovation
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <div className="px-6 md:px-20 py-16">
        {/* History Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            The <strong>Department of Food Science and Technology (FST)</strong> at 
            <strong> Federal Polytechnic Bida (FPB)</strong> was established following a successful 
            <strong> NBTE resource inspection in November 2023</strong>. The department commenced 
            full academic activities in the <strong>2023/2024 academic session</strong> and will 
            produce its first set of <strong>National Diploma (ND)</strong> graduates at the end 
            of the <strong>2024/2025 session</strong>. Plans are already in place to begin the 
            <strong> Higher National Diploma (HND)</strong> program in the near future.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            The National Diploma program in Food Science and Technology was designed to produce 
            food preservation and processing technicians capable of complementing the work of 
            food scientists and engineers both in the field and in the industry. Students are 
            trained to address modern challenges in food quality, processing, and safety using 
            innovative approaches and sustainable practices.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed">
            The department’s entry requirements include five O’Level credits in Mathematics, 
            English, Biology, Physics, and Chemistry, alongside a qualifying UTME score. 
            The ND program is a two-year terminal course structured to produce skilled, 
            job-ready graduates for the food sector.
          </p>
        </motion.section>

        {/* HOD Section */}
        <motion.section
          className="grid md:grid-cols-3 gap-8 items-center bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="md:col-span-1 flex justify-center">
            <img
              src="/images/hodfst.jpeg"
              alt="HOD"
              className="w-40 h-40 rounded-full object-cover shadow-lg border-4 border-blue-200"
            />
          </div>
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Head of Department's Speech
            </h2>
            <p className="text-justify text-gray-600 leading-relaxed mb-4">
              Dear students, colleagues, and visitors, it is my great pleasure to welcome you to the 
              <strong> Department of Food Science and Technology</strong> at 
              <strong> Federal Polytechnic Bida</strong>. As the pioneer Head of Department, I am 
              excited to lead a team of dedicated professionals committed to training students who 
              will revolutionize the food industry through research, innovation, and ethical practice.
            </p>
            <p className="text-justify text-gray-600 leading-relaxed mb-4">
              Our goal is to provide quality education that equips students with technical skills and 
              scientific understanding of food preservation, processing, and safety. We are building 
              a legacy of excellence where students can become catalysts for food security, nutrition 
              improvement, and sustainable development both locally and globally.
            </p>
            <p className="text-justify text-gray-600 leading-relaxed">
              I encourage all students to embrace curiosity, discipline, and passion for learning. 
              Together, we can shape a healthier and more food-secure future for our society.
            </p>
            <p className="mt-4 text-blue-700 font-semibold">— Mrs. Ejim, Millicent Nwanneamaka</p>
            <p className="text-gray-500">Head of Department</p>
          </div>
        </motion.section>

        {/* Vision & Mission Section */}
        <motion.section
          className="grid md:grid-cols-2 gap-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Vision</h2>
            <p className="text-justify text-gray-600 leading-relaxed">
              To be a <strong>leading center of excellence</strong> in 
              <strong> food science, technology, and research</strong>, committed to producing 
              innovative graduates who contribute to national and global food security, nutrition, 
              and industrial advancement.
            </p>
          </div>
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h2>
            <p className="text-justify text-gray-600 leading-relaxed">
              To provide <strong>quality education and hands-on training</strong> in food science 
              and technology, fostering innovation, professionalism, and entrepreneurship. 
              The department aims to equip students with skills and values that enable them 
              to ensure food safety, drive technological progress, and make meaningful 
              contributions to the nation’s agro-industrial development.
            </p>
          </div>
        </motion.section>

        {/* Why Study Food Science Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Why Study Food Science and Technology?
          </h2>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            Food is one of the most essential human needs on earth, and the field of 
            <strong> Food Science and Technology</strong> plays a vital role in ensuring the 
            <strong> safety, quality, and sustainability</strong> of what we consume. 
            This discipline is central to <strong>public health</strong> and 
            <strong> food security</strong>, bridging the gap between science, innovation, 
            and consumer well-being.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            By studying Food Science and Technology, students gain a deeper understanding of 
            how food is produced, preserved, processed, and distributed. It opens opportunities 
            to create new food products, improve existing ones, and address 
            <strong> nutritional deficiencies</strong> and <strong> malnutrition</strong> 
            through innovative solutions.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed">
            The program empowers students to contribute meaningfully to global efforts in 
            <strong> combating hunger</strong>, enhancing agricultural practices, and promoting 
            <strong> sustainable food systems</strong> that benefit society and the environment.
          </p>
        </motion.section>

        {/* Career & Entry Requirements Section */}
        <motion.section
          className="bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Career Opportunities and Entry Requirements
          </h2>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            Food Science and Technology offers a wide range of career paths in 
            <strong> food manufacturing, research and development, quality assurance, 
            and regulatory services</strong>. Graduates from the department can work in 
            government agencies, industries, and academic institutions, or build 
            successful careers as entrepreneurs in the food value chain.
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4">
            <li>Agricultural Research Institutes (Cereal, Cocoa, Palm Oil, and Tuber Research)</li>
            <li>National Agency for Food and Drug Administration and Control (NAFDAC)</li>
            <li>Standards Organisation of Nigeria (SON)</li>
            <li>Consumer Protection Council (CPC)</li>
            <li>Food and Beverage Manufacturing Industries</li>
            <li>Pharmaceutical Companies</li>
            <li>Educational Institutions (Polytechnics, Universities, and Secondary Schools)</li>
            <li>Entrepreneurship in Food Processing and Preservation</li>
          </ul>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Entry Requirements</h3>
          <p className="text-justify text-gray-600 leading-relaxed">
            Candidates must possess <strong>five O’Level credit passes</strong> in 
            Mathematics, English, Biology, Physics, and Chemistry obtained from 
            WAEC, NECO, or equivalent, along with a qualifying score in UTME. 
            The ND program spans <strong>two years (four semesters)</strong> and is designed 
            to prepare students for technical and professional roles in the food industry.
          </p>
        </motion.section>

        {/* Gallery Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Department Gallery
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["fst1.jpeg", "fst2.jpeg", "fst3.jpeg", "fst4.jpeg"].map(
              (img, i) => (
                <img
                  key={i}
                  src={`/images/${img}`}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-40 md:h-48 object-cover rounded-lg shadow-md hover:scale-105 transition-transform"
                />
              )
            )}
          </div>
        </motion.section>

        {/* Contact Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contact the Department
          </h2>
          <p className="text-gray-600 mb-2">
            <strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Email:</strong> dept.fst@fedpolybida.edu.ng
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Phone:</strong> +234 803 596 8340
          </p>
        </motion.section>

        {/* Map Section */}
        <motion.section
          className="rounded-2xl overflow-hidden shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <iframe
            title="Department Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3926.278884327274!2d6.0125!3d9.0820!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104965e46b2e11f5%3A0xdeadbeef123456!2sFederal%20Polytechnic%20Bida!5e0!3m2!1sen!2sng!4v0000000000000"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.section>
      </div>
    </div>
  );
};

export default FoodScience;
